// src/pages/customer-panel/signatures/SignDocumentPage.js
// Customer Panel - View/Sign a document via token (authenticated users)

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axiosInstance from '../../../utils/axiosInstance';
import SignatureCanvas from 'react-signature-canvas';
import Draggable from 'react-draggable';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import {
    Box,
    Typography,
    Paper,
    Button,
    Alert,
    CircularProgress,
    Chip,
    Tabs,
    Tab,
    TextField,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    Tooltip,
} from '@mui/material';
import DrawIcon from '@mui/icons-material/Draw';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import DescriptionIcon from '@mui/icons-material/Description';
import PersonIcon from '@mui/icons-material/Person';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import UndoIcon from '@mui/icons-material/Undo';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import ReplayIcon from '@mui/icons-material/Replay';
import OpenWithIcon from '@mui/icons-material/OpenWith';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";
const GREEN_ACCENT = '#10b981';
const GREEN_DARK = '#059669';
const GREEN_LIGHT = '#d1fae5';

const BASE_SIGNATURE_SIZE = 150;
const INITIAL_SIGNATURE_SIZE = 75;

function generateSignatureImage(text, isInitial = false) {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const displayText = isInitial ? text.charAt(0).toUpperCase() : text;
    const fontSize = isInitial ? 60 : 50;
    const fontFace = isInitial ? "'Times New Roman', serif" : "'Great Vibes', 'Georgia', cursive, serif";
    ctx.font = `${fontSize}px ${fontFace}`;
    const metrics = ctx.measureText(displayText);
    canvas.width = Math.ceil(metrics.width) + 20;
    canvas.height = fontSize + 30;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.font = `${fontSize}px ${fontFace}`;
    ctx.fillStyle = '#1e293b';
    ctx.textBaseline = 'top';
    ctx.fillText(displayText, 10, 10);
    return canvas.toDataURL('image/png');
}

function getImageDimensions(dataUrl, targetWidth) {
    return new Promise((resolve) => {
        const img = new Image();
        img.onload = () => {
            const aspect = img.width / img.height;
            resolve({ width: targetWidth, height: targetWidth / aspect });
        };
        img.onerror = () => resolve({ width: targetWidth, height: targetWidth * 0.4 });
        img.src = dataUrl;
    });
}

export default function SignDocumentPage() {
    const { token } = useParams();
    const navigate = useNavigate();

    const [requestData, setRequestData] = useState(null);
    const [documentData, setDocumentData] = useState(null);
    const [pdfData, setPdfData] = useState(null);
    const [viewerRole, setViewerRole] = useState(null);
    const [signatureZones, setSignatureZones] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    // PDF state
    const [numPages, setNumPages] = useState(0);
    const [pageDimensions, setPageDimensions] = useState({});
    const pageRefs = useRef([]);

    // Signature creation state
    const [signatureMode, setSignatureMode] = useState(0); // 0=draw, 1=type, 2=upload
    const [typedSignature, setTypedSignature] = useState('');
    const [uploadedImage, setUploadedImage] = useState(null);
    const sigCanvasRef = useRef(null);

    // Placement state
    const [isPlacing, setIsPlacing] = useState(false);
    const [placedSignatures, setPlacedSignatures] = useState([]);

    // Signing state
    const [signing, setSigning] = useState(false);
    const [signed, setSigned] = useState(false);
    const [signError, setSignError] = useState('');

    // Admin actions
    const [adminSignDialogOpen, setAdminSignDialogOpen] = useState(false);
    const [actionLoading, setActionLoading] = useState(false);

    useEffect(() => {
        if (token) fetchDocument();
    }, [token]);

    // Convert document data to ArrayBuffer for react-pdf
    useEffect(() => {
        if (!documentData) return;
        if (documentData.startsWith('data:')) {
            // Base64 data URL
            const base64 = documentData.split(',')[1];
            const binary = atob(base64);
            const bytes = new Uint8Array(binary.length);
            for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
            setPdfData({ data: bytes });
        } else {
            // URL - fetch as arraybuffer
            fetch(documentData)
                .then((res) => res.arrayBuffer())
                .then((buf) => setPdfData({ data: new Uint8Array(buf) }))
                .catch(() => setPdfData(null));
        }
    }, [documentData]);

    const fetchDocument = async () => {
        setLoading(true);
        setError('');
        try {
            const response = await axiosInstance.get(`/signatures/${token}`);
            const data = response.data;
            if (data?.success) {
                setRequestData(data.data?.request || null);
                setDocumentData(data.data?.document_data || null);
                setViewerRole(data.viewer_role || 'primary');
                setSignatureZones(data.data?.request?.signature_zones || []);
            } else {
                setError(data?.message || 'Failed to load document.');
            }
        } catch (err) {
            console.error('Error fetching document:', err);
            const status = err.response?.status;
            if (status === 404) setError('Document not found. The signing link may be invalid.');
            else if (status === 403) setError(err.response?.data?.message || 'You do not have permission to view this document.');
            else setError(err.response?.data?.message || 'Failed to load the document.');
        } finally {
            setLoading(false);
        }
    };

    const onDocumentLoadSuccess = useCallback(({ numPages: n }) => {
        setNumPages(n);
    }, []);

    const measurePage = useCallback((pageNumber, pageRef) => {
        if (!pageRef) return;
        setTimeout(() => {
            const canvas = pageRef.querySelector('canvas');
            if (canvas) {
                setPageDimensions((prev) => ({
                    ...prev,
                    [pageNumber]: { width: canvas.clientWidth, height: canvas.clientHeight },
                }));
            }
        }, 150);
    }, []);

    const clearSignature = () => {
        if (signatureMode === 0 && sigCanvasRef.current) {
            sigCanvasRef.current.clear();
        }
        setTypedSignature('');
        setUploadedImage(null);
    };

    const handleUploadSignature = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (!['image/png', 'image/jpeg'].includes(file.type)) {
            setSignError('Please upload a PNG or JPEG image.');
            return;
        }
        if (file.size > 2 * 1024 * 1024) {
            setSignError('Image must be under 2MB.');
            return;
        }
        const reader = new FileReader();
        reader.onload = () => setUploadedImage(reader.result);
        reader.readAsDataURL(file);
    };

    const handleCreateSignature = async () => {
        let fullSignatureUrl = null;
        let initialSignatureUrl = null;

        if (signatureMode === 0) {
            // Draw mode
            if (!sigCanvasRef.current || sigCanvasRef.current.isEmpty()) {
                setSignError('Please draw your signature.');
                return;
            }
            fullSignatureUrl = sigCanvasRef.current.getTrimmedCanvas().toDataURL('image/png');
        } else if (signatureMode === 1) {
            // Type mode
            if (!typedSignature.trim()) {
                setSignError('Please type your name.');
                return;
            }
            fullSignatureUrl = generateSignatureImage(typedSignature.trim(), false);
            initialSignatureUrl = generateSignatureImage(typedSignature.trim(), true);
        } else if (signatureMode === 2) {
            // Upload mode
            if (!uploadedImage) {
                setSignError('Please upload a signature image.');
                return;
            }
            fullSignatureUrl = uploadedImage;
        }

        setSignError('');

        // Determine which zones apply to this viewer
        const myZones = signatureZones.filter((z) =>
            viewerRole === 'primary' ? z.signer === 'primary' : z.signer === 'additional'
        );

        if (myZones.length === 0) {
            // No zones - place at default position on page 1
            const dims = await getImageDimensions(fullSignatureUrl, BASE_SIGNATURE_SIZE);
            const pageDim = pageDimensions[1] || { width: 612, height: 792 };
            setPlacedSignatures([{
                imageDataUrl: fullSignatureUrl,
                page: 1,
                zoneIndex: -1,
                position: { x: pageDim.width * 0.25, y: pageDim.height * 0.8 },
                dimensions: dims,
            }]);
        } else {
            const placed = [];
            for (let i = 0; i < myZones.length; i++) {
                const zone = myZones[i];
                const isFirst = i === 0;
                const sigUrl = !isFirst && initialSignatureUrl ? initialSignatureUrl : fullSignatureUrl;
                const targetSize = isFirst ? BASE_SIGNATURE_SIZE : INITIAL_SIGNATURE_SIZE;
                const dims = await getImageDimensions(sigUrl, targetSize);
                const pageDim = pageDimensions[zone.page] || { width: 612, height: 792 };

                // Center signature within zone
                const zoneX = (zone.x_percent / 100) * pageDim.width;
                const zoneY = (zone.y_percent / 100) * pageDim.height;
                const zoneW = (zone.width_percent / 100) * pageDim.width;
                const zoneH = (zone.height_percent / 100) * pageDim.height;

                placed.push({
                    imageDataUrl: sigUrl,
                    page: zone.page || 1,
                    zoneIndex: i,
                    position: {
                        x: zoneX + (zoneW - dims.width) / 2,
                        y: zoneY + (zoneH - dims.height) / 2,
                    },
                    dimensions: dims,
                });
            }
            setPlacedSignatures(placed);
        }

        setIsPlacing(true);
    };

    const handleDragStop = (idx, data) => {
        setPlacedSignatures((prev) =>
            prev.map((sig, i) => (i === idx ? { ...sig, position: { x: data.x, y: data.y } } : sig))
        );
    };

    const handleStartOver = () => {
        setIsPlacing(false);
        setPlacedSignatures([]);
        clearSignature();
    };

    const handleSubmitSignatures = async () => {
        setSigning(true);
        setSignError('');
        try {
            const signatures = placedSignatures.map((sig) => {
                const pageDim = pageDimensions[sig.page] || { width: 612, height: 792 };
                return {
                    signatureImage: sig.imageDataUrl,
                    pageNumber: sig.page,
                    position: sig.position,
                    dimensions: sig.dimensions,
                    pageDimensions: pageDim,
                };
            });
            await axiosInstance.post(`/signatures/${token}/submit`, { signatures });
            setSigned(true);
        } catch (err) {
            console.error('Error signing document:', err);
            setSignError(err.response?.data?.message || 'Failed to sign the document.');
        } finally {
            setSigning(false);
        }
    };

    const handleAdminSign = async () => {
        let sigImage = null;
        if (signatureMode === 0 && sigCanvasRef.current && !sigCanvasRef.current.isEmpty()) {
            sigImage = sigCanvasRef.current.getTrimmedCanvas().toDataURL('image/png');
        } else if (signatureMode === 1 && typedSignature.trim()) {
            sigImage = generateSignatureImage(typedSignature.trim(), false);
        } else if (signatureMode === 2 && uploadedImage) {
            sigImage = uploadedImage;
        }

        if (!sigImage) {
            setSignError('Please create your signature before submitting.');
            return;
        }

        setActionLoading(true);
        setSignError('');
        try {
            const pageDim = pageDimensions[1] || { width: 612, height: 792 };
            const payload = {
                signatureImage: sigImage,
                pageNumber: 1,
                position: { x: pageDim.width * 0.25, y: pageDim.height * 0.85 },
                dimensions: { width: pageDim.width * 0.5, height: 60 },
                pageDimensions: pageDim,
            };
            await axiosInstance.post(`/signatures/${token}/admin-sign`, { signature: payload });
            setAdminSignDialogOpen(false);
            clearSignature();
            fetchDocument();
        } catch (err) {
            setSignError(err.response?.data?.message || 'Failed to apply admin signature.');
        } finally {
            setActionLoading(false);
        }
    };

    const handleStatusAction = async (newStatus) => {
        setActionLoading(true);
        try {
            await axiosInstance.put(`/signatures/${token}/status`, { status: newStatus });
            fetchDocument();
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to update status.');
        } finally {
            setActionLoading(false);
        }
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '--';
        try {
            return new Date(dateStr).toLocaleDateString([], { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
        } catch { return '--'; }
    };

    // --- Loading ---
    if (loading) {
        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '60vh', p: 3 }}>
                <CircularProgress sx={{ color: GREEN_ACCENT, mb: 2 }} />
                <Typography sx={{ fontFamily, color: '#64748b' }}>Loading document...</Typography>
            </Box>
        );
    }

    // --- Error ---
    if (error && !requestData) {
        return (
            <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 600, mx: 'auto', mt: 4 }}>
                <Paper elevation={0} sx={{ p: 4, borderRadius: '16px', border: '1px solid #fecaca', textAlign: 'center' }}>
                    <ErrorOutlineIcon sx={{ fontSize: 56, color: '#ef4444', mb: 2 }} />
                    <Typography variant="h6" sx={{ fontFamily, fontWeight: 700, color: '#1e293b', mb: 1 }}>Unable to Load Document</Typography>
                    <Typography sx={{ fontFamily, color: '#64748b', mb: 3 }}>{error}</Typography>
                    <Button variant="outlined" startIcon={<ArrowBackIcon />}
                        onClick={() => navigate('/customer-panel/signatures')}
                        sx={{ fontFamily, textTransform: 'none', borderColor: GREEN_ACCENT, color: GREEN_ACCENT, '&:hover': { borderColor: GREEN_DARK, bgcolor: GREEN_LIGHT }, borderRadius: '10px' }}>
                        Back to Signatures
                    </Button>
                </Paper>
            </Box>
        );
    }

    // --- Signed success ---
    if (signed) {
        return (
            <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 600, mx: 'auto', mt: 4 }}>
                <Paper elevation={0} sx={{ p: 5, borderRadius: '16px', border: `1px solid ${GREEN_ACCENT}`, textAlign: 'center', background: `linear-gradient(180deg, ${GREEN_LIGHT}40 0%, #ffffff 100%)` }}>
                    <Box sx={{ width: 72, height: 72, borderRadius: '50%', bgcolor: GREEN_LIGHT, display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 2 }}>
                        <CheckCircleOutlineIcon sx={{ fontSize: 40, color: GREEN_ACCENT }} />
                    </Box>
                    <Typography variant="h5" sx={{ fontFamily, fontWeight: 700, color: '#1e293b', mb: 1 }}>Document Signed Successfully</Typography>
                    <Typography sx={{ fontFamily, color: '#64748b', mb: 3 }}>Your signature has been recorded. All parties will be notified.</Typography>
                    <Button variant="contained" onClick={() => navigate('/customer-panel/signatures')}
                        sx={{ fontFamily, textTransform: 'none', fontWeight: 600, bgcolor: GREEN_ACCENT, '&:hover': { bgcolor: GREEN_DARK }, borderRadius: '10px', px: 4 }}>
                        Go to Signatures
                    </Button>
                </Paper>
            </Box>
        );
    }

    const status = requestData?.status || '';
    const isAlreadySigned = status === 'signed' || status === 'completed';
    const canSign = (viewerRole === 'primary' || viewerRole === 'additional') && (status === 'pending_signature' || status === 'resign_requested');
    const isAdminView = viewerRole === 'admin';

    // Get zones for this viewer
    const myZones = signatureZones.filter((z) =>
        viewerRole === 'primary' ? z.signer === 'primary' : z.signer === 'additional'
    );

    // Signature creation panel (shared between main page and admin dialog)
    const renderSignatureCreator = (isAdmin = false) => (
        <>
            <Tabs value={signatureMode} onChange={(_, v) => { setSignatureMode(v); clearSignature(); }}
                sx={{ mb: 2, '& .MuiTab-root': { fontFamily, textTransform: 'none', fontWeight: 600, minHeight: 36 }, '& .Mui-selected': { color: GREEN_ACCENT }, '& .MuiTabs-indicator': { backgroundColor: GREEN_ACCENT } }}>
                <Tab label="Draw" />
                <Tab label="Type" />
                <Tab label="Upload" />
            </Tabs>

            {/* Draw mode */}
            {signatureMode === 0 && (
                <Box sx={{ border: '2px dashed #e2e8f0', borderRadius: '12px', overflow: 'hidden', mb: 2, position: 'relative' }}>
                    <SignatureCanvas ref={sigCanvasRef} penColor="#1e293b"
                        canvasProps={{ width: isAdmin ? 450 : 550, height: 150, style: { width: '100%', height: 150, background: '#fafafa' } }} />
                    <Tooltip title="Clear">
                        <IconButton size="small" onClick={clearSignature}
                            sx={{ position: 'absolute', top: 8, right: 8, bgcolor: '#fff', boxShadow: 1, '&:hover': { bgcolor: '#f1f5f9' } }}>
                            <UndoIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Box>
            )}

            {/* Type mode */}
            {signatureMode === 1 && (
                <>
                    <TextField fullWidth placeholder="Type your full name here..."
                        value={typedSignature} onChange={(e) => setTypedSignature(e.target.value)}
                        variant="outlined"
                        sx={{ mb: 2, '& .MuiOutlinedInput-root': { borderRadius: '12px', fontFamily: "'Great Vibes', 'Georgia', cursive, serif", fontSize: '1.4rem', '&.Mui-focused fieldset': { borderColor: GREEN_ACCENT } } }} />
                    {typedSignature.trim() && (
                        <Paper elevation={0} sx={{ p: 2, mb: 2, bgcolor: '#f8fafc', borderRadius: '10px', border: '1px dashed #e2e8f0' }}>
                            <Box sx={{ display: 'flex', gap: 3, justifyContent: 'center', alignItems: 'center' }}>
                                <Box sx={{ textAlign: 'center' }}>
                                    <Typography variant="caption" sx={{ fontFamily, color: '#94a3b8', display: 'block', mb: 0.5 }}>Signature</Typography>
                                    <Typography sx={{ fontFamily: "'Great Vibes', 'Georgia', cursive, serif", fontSize: '2rem', color: '#1e293b' }}>{typedSignature}</Typography>
                                </Box>
                                <Box sx={{ textAlign: 'center' }}>
                                    <Typography variant="caption" sx={{ fontFamily, color: '#94a3b8', display: 'block', mb: 0.5 }}>Initial</Typography>
                                    <Typography sx={{ fontFamily: "'Times New Roman', serif", fontSize: '2rem', color: '#1e293b', fontWeight: 700 }}>{typedSignature.charAt(0).toUpperCase()}</Typography>
                                </Box>
                            </Box>
                        </Paper>
                    )}
                </>
            )}

            {/* Upload mode */}
            {signatureMode === 2 && (
                <Box sx={{ mb: 2 }}>
                    {!uploadedImage ? (
                        <Box
                            component="label"
                            sx={{
                                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                                border: '2px dashed #e2e8f0', borderRadius: '12px', p: 3, cursor: 'pointer',
                                '&:hover': { borderColor: GREEN_ACCENT, bgcolor: '#f0fdf4' },
                                transition: 'all 0.2s',
                            }}
                        >
                            <CloudUploadIcon sx={{ fontSize: 36, color: '#94a3b8', mb: 1 }} />
                            <Typography sx={{ fontFamily, fontSize: '0.85rem', color: '#64748b', mb: 0.5 }}>Click to upload signature image</Typography>
                            <Typography sx={{ fontFamily, fontSize: '0.7rem', color: '#94a3b8' }}>PNG or JPEG, max 2MB</Typography>
                            <input type="file" accept="image/png,image/jpeg" hidden onChange={handleUploadSignature} />
                        </Box>
                    ) : (
                        <Paper elevation={0} sx={{ p: 2, bgcolor: '#f8fafc', borderRadius: '10px', border: '1px dashed #e2e8f0', textAlign: 'center', position: 'relative' }}>
                            <IconButton size="small" onClick={() => setUploadedImage(null)}
                                sx={{ position: 'absolute', top: 4, right: 4, bgcolor: '#fee2e2', '&:hover': { bgcolor: '#fecaca' } }}>
                                <DeleteIcon fontSize="small" sx={{ color: '#ef4444' }} />
                            </IconButton>
                            <Typography variant="caption" sx={{ fontFamily, color: '#94a3b8', display: 'block', mb: 1 }}>Uploaded Signature</Typography>
                            <img src={uploadedImage} alt="Signature" style={{ maxHeight: 100, maxWidth: '80%', objectFit: 'contain' }} />
                        </Paper>
                    )}
                </Box>
            )}
        </>
    );

    return (
        <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 900, mx: 'auto' }}>
            {/* Header */}
            <Paper elevation={0} sx={{ p: 2.5, px: 3, mb: 3, borderRadius: '16px', background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)', color: '#fff' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <DrawIcon sx={{ fontSize: 28 }} />
                        <Box>
                            <Typography variant="h6" sx={{ fontFamily, fontWeight: 700 }}>
                                {isAdminView ? 'Review Document' : 'Sign Document'}
                            </Typography>
                            <Typography variant="body2" sx={{ fontFamily, opacity: 0.9 }}>
                                {isAdminView ? 'Review the signature status and manage this request' : 'Review the document and provide your signature'}
                            </Typography>
                        </Box>
                    </Box>
                    <Button variant="outlined" startIcon={<ArrowBackIcon />}
                        onClick={() => navigate('/customer-panel/signatures')}
                        sx={{ fontFamily, textTransform: 'none', borderColor: 'rgba(255,255,255,0.5)', color: '#fff', '&:hover': { borderColor: '#fff', bgcolor: 'rgba(255,255,255,0.1)' }, borderRadius: '10px', fontSize: '0.8rem' }}>
                        Back
                    </Button>
                </Box>
            </Paper>

            {/* Document Info Card */}
            <Paper elevation={0} sx={{ p: 3, mb: 3, borderRadius: '14px', border: '1px solid #e2e8f0' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6" sx={{ fontFamily, fontWeight: 700, color: '#1e293b' }}>Document Details</Typography>
                    <Chip label={status.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())} size="small"
                        sx={{ fontFamily, fontWeight: 600, fontSize: '0.7rem', bgcolor: isAlreadySigned ? GREEN_LIGHT : '#fef3c7', color: isAlreadySigned ? GREEN_DARK : '#92400e' }} />
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <DescriptionIcon sx={{ fontSize: 20, color: '#94a3b8' }} />
                        <Box>
                            <Typography variant="caption" sx={{ fontFamily, color: '#94a3b8', display: 'block' }}>Document</Typography>
                            <Typography sx={{ fontFamily, fontWeight: 600, color: '#1e293b', fontSize: '0.9rem' }}>{requestData?.original_filename || 'Untitled'}</Typography>
                        </Box>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <PersonIcon sx={{ fontSize: 20, color: '#94a3b8' }} />
                        <Box>
                            <Typography variant="caption" sx={{ fontFamily, color: '#94a3b8', display: 'block' }}>
                                {isAdminView ? 'Signer' : 'Requested by'}
                            </Typography>
                            <Typography sx={{ fontFamily, fontWeight: 500, color: '#1e293b', fontSize: '0.9rem' }}>
                                {isAdminView ? `${requestData?.user || ''} (${requestData?.user_email || ''})` : (requestData?.requester || '')}
                            </Typography>
                        </Box>
                    </Box>
                    {requestData?.created_at && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <CalendarTodayIcon sx={{ fontSize: 20, color: '#94a3b8' }} />
                            <Box>
                                <Typography variant="caption" sx={{ fontFamily, color: '#94a3b8', display: 'block' }}>Sent on</Typography>
                                <Typography sx={{ fontFamily, fontWeight: 500, color: '#1e293b', fontSize: '0.9rem' }}>{formatDate(requestData.created_at)}</Typography>
                            </Box>
                        </Box>
                    )}
                    {requestData?.additional_signer_name && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <PersonIcon sx={{ fontSize: 20, color: '#94a3b8' }} />
                            <Box>
                                <Typography variant="caption" sx={{ fontFamily, color: '#94a3b8', display: 'block' }}>Additional Signer</Typography>
                                <Typography sx={{ fontFamily, fontWeight: 500, color: '#1e293b', fontSize: '0.9rem' }}>
                                    {requestData.additional_signer_name} ({requestData.additional_signer_email})
                                    {requestData.additional_signed_at ? ' - Signed' : ' - Pending'}
                                </Typography>
                            </Box>
                        </Box>
                    )}
                </Box>
            </Paper>

            {/* PDF Viewer with Zones & Draggable Signatures */}
            {pdfData && (
                <Paper elevation={0} sx={{ mb: 3, borderRadius: '14px', border: '1px solid #e2e8f0', overflow: 'hidden' }}>
                    <Box sx={{ p: 2, bgcolor: '#f8fafc', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box>
                            <Typography sx={{ fontFamily, fontWeight: 600, color: '#475569', fontSize: '0.85rem' }}>Document Preview</Typography>
                            {myZones.length > 0 && !isPlacing && (
                                <Typography variant="caption" sx={{ fontFamily, color: '#94a3b8' }}>
                                    {myZones.length} signature zone(s) defined
                                </Typography>
                            )}
                            {isPlacing && (
                                <Typography variant="caption" sx={{ fontFamily, color: GREEN_ACCENT }}>
                                    Drag to adjust signature positions, then confirm below
                                </Typography>
                            )}
                        </Box>
                    </Box>
                    <Box sx={{ maxHeight: '70vh', overflowY: 'auto', bgcolor: '#f1f5f9', p: 2 }}>
                        <Document file={pdfData} onLoadSuccess={onDocumentLoadSuccess}>
                            {Array.from({ length: numPages }, (_, i) => {
                                const pageNumber = i + 1;
                                const pageDim = pageDimensions[pageNumber];
                                const zonesForPage = myZones
                                    .map((z, idx) => ({ ...z, globalIndex: idx }))
                                    .filter((z) => (z.page || 1) === pageNumber);
                                const sigsForPage = placedSignatures.filter((s) => s.page === pageNumber);

                                return (
                                    <Box
                                        key={pageNumber}
                                        ref={(el) => {
                                            pageRefs.current[i] = el;
                                            if (el) measurePage(pageNumber, el);
                                        }}
                                        sx={{ position: 'relative', mb: 2, display: 'inline-block', boxShadow: 2 }}
                                    >
                                        <Page pageNumber={pageNumber} />

                                        {/* Zone indicators (shown when NOT placing) */}
                                        {!isPlacing && canSign && pageDim && zonesForPage.map((zone) => (
                                            <Box
                                                key={`zone-${zone.globalIndex}`}
                                                sx={{
                                                    position: 'absolute',
                                                    left: `${zone.x_percent}%`,
                                                    top: `${zone.y_percent}%`,
                                                    width: `${zone.width_percent}%`,
                                                    height: `${zone.height_percent}%`,
                                                    border: `2px dashed ${GREEN_ACCENT}`,
                                                    bgcolor: `${GREEN_ACCENT}15`,
                                                    pointerEvents: 'none',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                }}
                                            >
                                                <Typography sx={{
                                                    fontFamily, fontSize: '0.75rem', fontWeight: 600,
                                                    color: GREEN_ACCENT, textTransform: 'uppercase',
                                                }}>
                                                    {zone.globalIndex === 0 ? 'Sign Here' : 'Initial Here'}
                                                </Typography>
                                            </Box>
                                        ))}

                                        {/* Draggable placed signatures */}
                                        {isPlacing && pageDim && sigsForPage.map((sig) => {
                                            const sigIdx = placedSignatures.indexOf(sig);
                                            const zone = sig.zoneIndex >= 0 ? myZones[sig.zoneIndex] : null;

                                            // Compute bounds
                                            let bounds;
                                            if (zone) {
                                                bounds = {
                                                    left: (zone.x_percent / 100) * pageDim.width,
                                                    top: (zone.y_percent / 100) * pageDim.height,
                                                    right: ((zone.x_percent + zone.width_percent) / 100) * pageDim.width - sig.dimensions.width,
                                                    bottom: ((zone.y_percent + zone.height_percent) / 100) * pageDim.height - sig.dimensions.height,
                                                };
                                            } else {
                                                bounds = {
                                                    left: 0, top: 0,
                                                    right: pageDim.width - sig.dimensions.width,
                                                    bottom: pageDim.height - sig.dimensions.height,
                                                };
                                            }

                                            return (
                                                <Draggable
                                                    key={`sig-${sigIdx}`}
                                                    bounds={bounds}
                                                    position={sig.position}
                                                    onStop={(_, data) => handleDragStop(sigIdx, data)}
                                                >
                                                    <Box sx={{
                                                        position: 'absolute', left: 0, top: 0,
                                                        cursor: 'grab', '&:active': { cursor: 'grabbing' },
                                                    }}>
                                                        <img
                                                            src={sig.imageDataUrl}
                                                            alt="Signature"
                                                            style={{
                                                                width: sig.dimensions.width,
                                                                height: sig.dimensions.height,
                                                                pointerEvents: 'none',
                                                            }}
                                                        />
                                                        <Box sx={{
                                                            position: 'absolute', top: -8, left: -8,
                                                            width: 20, height: 20, borderRadius: '50%',
                                                            bgcolor: '#1e293b', display: 'flex',
                                                            alignItems: 'center', justifyContent: 'center',
                                                            boxShadow: 1,
                                                        }}>
                                                            <OpenWithIcon sx={{ fontSize: 12, color: '#fff' }} />
                                                        </Box>
                                                    </Box>
                                                </Draggable>
                                            );
                                        })}

                                        {/* Page label */}
                                        <Typography sx={{
                                            position: 'absolute', bottom: 4, right: 8,
                                            fontFamily, fontSize: '0.7rem', color: '#94a3b8',
                                            bgcolor: 'rgba(255,255,255,0.8)', px: 1, borderRadius: '4px',
                                        }}>
                                            Page {pageNumber}
                                        </Typography>
                                    </Box>
                                );
                            })}
                        </Document>
                    </Box>
                </Paper>
            )}

            {/* Fallback if no PDF data */}
            {!pdfData && documentData && (
                <Paper elevation={0} sx={{ mb: 3, borderRadius: '14px', border: '1px solid #e2e8f0', overflow: 'hidden' }}>
                    <Box sx={{ p: 2, bgcolor: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                        <Typography sx={{ fontFamily, fontWeight: 600, color: '#475569', fontSize: '0.85rem' }}>Document Preview</Typography>
                    </Box>
                    <Box sx={{ width: '100%', height: 600, bgcolor: '#f1f5f9' }}>
                        <embed src={documentData} type="application/pdf" width="100%" height="100%" style={{ border: 'none' }} />
                    </Box>
                </Paper>
            )}

            {/* Admin Actions */}
            {isAdminView && (
                <Paper elevation={0} sx={{ p: 3, mb: 3, borderRadius: '14px', border: '1px solid #e2e8f0' }}>
                    <Typography variant="h6" sx={{ fontFamily, fontWeight: 700, color: '#1e293b', mb: 2 }}>Admin Actions</Typography>
                    <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                        <Button variant="outlined" startIcon={<DrawIcon />}
                            onClick={() => { clearSignature(); setAdminSignDialogOpen(true); }}
                            sx={{ fontFamily, textTransform: 'none', borderColor: GREEN_ACCENT, color: GREEN_ACCENT, '&:hover': { borderColor: GREEN_DARK, bgcolor: GREEN_LIGHT }, borderRadius: '10px' }}>
                            Add Admin Signature
                        </Button>
                        {status === 'signed' && (
                            <>
                                <Button variant="contained" startIcon={actionLoading ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : <DoneAllIcon />}
                                    onClick={() => handleStatusAction('completed')} disabled={actionLoading}
                                    sx={{ fontFamily, textTransform: 'none', fontWeight: 600, bgcolor: GREEN_ACCENT, '&:hover': { bgcolor: GREEN_DARK }, borderRadius: '10px' }}>
                                    Mark Complete
                                </Button>
                                <Button variant="outlined" startIcon={<ReplayIcon />}
                                    onClick={() => handleStatusAction('resign_requested')} disabled={actionLoading}
                                    sx={{ fontFamily, textTransform: 'none', borderColor: '#f59e0b', color: '#f59e0b', '&:hover': { borderColor: '#d97706', bgcolor: '#fef3c7' }, borderRadius: '10px' }}>
                                    Request Re-sign
                                </Button>
                            </>
                        )}
                    </Box>
                </Paper>
            )}

            {/* Placement Confirmation Panel */}
            {canSign && isPlacing && (
                <Paper elevation={0} sx={{ p: 3, borderRadius: '14px', border: `2px solid ${GREEN_ACCENT}`, bgcolor: '#f0fdf4' }}>
                    <Typography variant="h6" sx={{ fontFamily, fontWeight: 700, color: '#1e293b', mb: 0.5 }}>Confirm Signature Placement</Typography>
                    <Typography variant="body2" sx={{ fontFamily, color: '#64748b', mb: 2 }}>
                        Drag your signature(s) to adjust position within the designated zones. When you're satisfied, click "Apply & Finish".
                    </Typography>

                    {signError && (
                        <Alert severity="error" sx={{ mb: 2, fontFamily, borderRadius: '10px' }} onClose={() => setSignError('')}>{signError}</Alert>
                    )}

                    <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'flex-end' }}>
                        <Button variant="outlined" onClick={handleStartOver} disabled={signing}
                            sx={{ fontFamily, textTransform: 'none', borderColor: '#e2e8f0', color: '#64748b', '&:hover': { borderColor: '#cbd5e1', bgcolor: '#f8fafc' }, borderRadius: '10px' }}>
                            Start Over
                        </Button>
                        <Button variant="contained" startIcon={signing ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : <DrawIcon />}
                            onClick={handleSubmitSignatures} disabled={signing}
                            sx={{ fontFamily, textTransform: 'none', fontWeight: 700, bgcolor: GREEN_ACCENT, '&:hover': { bgcolor: GREEN_DARK }, '&.Mui-disabled': { bgcolor: '#e2e8f0', color: '#94a3b8' }, borderRadius: '10px', px: 3 }}>
                            {signing ? 'Signing...' : 'Apply & Finish'}
                        </Button>
                    </Box>
                </Paper>
            )}

            {/* Signature Creation Area (for signers, before placement) */}
            {canSign && !isPlacing && (
                <Paper elevation={0} sx={{ p: 3, borderRadius: '14px', border: '1px solid #e2e8f0' }}>
                    <Typography variant="h6" sx={{ fontFamily, fontWeight: 700, color: '#1e293b', mb: 0.5 }}>Your Signature</Typography>
                    <Typography variant="body2" sx={{ fontFamily, color: '#64748b', mb: 2 }}>
                        Draw, type, or upload your signature below.
                    </Typography>

                    {signError && (
                        <Alert severity="error" sx={{ mb: 2, fontFamily, borderRadius: '10px' }} onClose={() => setSignError('')}>{signError}</Alert>
                    )}

                    {renderSignatureCreator(false)}

                    {/* Legal disclaimer */}
                    <Typography variant="caption" sx={{ fontFamily, color: '#94a3b8', display: 'block', mb: 2, lineHeight: 1.5 }}>
                        By clicking "Place Signature" below, you agree that your electronic signature is the legal equivalent of your manual signature on this document.
                    </Typography>

                    <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'flex-end' }}>
                        <Button variant="outlined" onClick={() => navigate('/customer-panel/signatures')}
                            sx={{ fontFamily, textTransform: 'none', borderColor: '#e2e8f0', color: '#64748b', '&:hover': { borderColor: '#cbd5e1', bgcolor: '#f8fafc' }, borderRadius: '10px' }}>
                            Cancel
                        </Button>
                        <Button variant="contained" startIcon={<DrawIcon />}
                            onClick={handleCreateSignature}
                            sx={{ fontFamily, textTransform: 'none', fontWeight: 700, bgcolor: GREEN_ACCENT, '&:hover': { bgcolor: GREEN_DARK }, borderRadius: '10px', px: 3 }}>
                            Place Signature
                        </Button>
                    </Box>
                </Paper>
            )}

            {/* Already signed message */}
            {(viewerRole === 'primary' || viewerRole === 'additional') && isAlreadySigned && (
                <Paper elevation={0} sx={{ p: 3, borderRadius: '14px', border: `1px solid ${GREEN_ACCENT}`, textAlign: 'center' }}>
                    <CheckCircleOutlineIcon sx={{ fontSize: 40, color: GREEN_ACCENT, mb: 1 }} />
                    <Typography variant="h6" sx={{ fontFamily, fontWeight: 700, color: '#1e293b', mb: 0.5 }}>Document Already Signed</Typography>
                    <Typography variant="body2" sx={{ fontFamily, color: '#64748b' }}>
                        {status === 'completed' ? 'This document has been completed.' : 'You have already signed this document.'}
                    </Typography>
                </Paper>
            )}

            {/* Admin Sign Dialog */}
            <Dialog open={adminSignDialogOpen} onClose={() => !actionLoading && setAdminSignDialogOpen(false)}
                maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '14px' } }}>
                <DialogTitle sx={{ fontFamily, fontWeight: 700 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <DrawIcon sx={{ color: GREEN_ACCENT }} />
                        Add Admin Signature
                    </Box>
                </DialogTitle>
                <DialogContent>
                    {signError && (
                        <Alert severity="error" sx={{ mb: 2, fontFamily, borderRadius: '10px' }} onClose={() => setSignError('')}>{signError}</Alert>
                    )}
                    {renderSignatureCreator(true)}
                </DialogContent>
                <DialogActions sx={{ p: 2, pt: 0 }}>
                    <Button onClick={() => setAdminSignDialogOpen(false)} disabled={actionLoading}
                        sx={{ fontFamily, textTransform: 'none', color: '#64748b' }}>Cancel</Button>
                    <Button onClick={handleAdminSign} disabled={actionLoading} variant="contained"
                        startIcon={actionLoading ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : <DrawIcon />}
                        sx={{ fontFamily, textTransform: 'none', fontWeight: 600, bgcolor: GREEN_ACCENT, '&:hover': { bgcolor: GREEN_DARK }, borderRadius: '10px' }}>
                        {actionLoading ? 'Applying...' : 'Apply Signature'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
